import { useMutation, useQuery } from "@tanstack/react-query"
import { useLocation } from "react-router"

import {
  getAccountFunctionSegments,
  getAccountOfferings,
  getAccountResearchReport,
  getAccounts,
  getCollaborators,
  getLast5EmailTemplates,
  getOrbitshiftLanguages,
  getOrbitshiftLocations,
  getSavedTemplate,
  getTenantUsersBasicDetails,
  requestNewAccount
} from "~/api/accounts"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"
import { normalizeLocationFilterValues } from "~/utils/location-helpers"
import { queryClient } from "~/utils/query-client"

const DEFAULT_PAYLOAD = {
  searchText: "",
  industries: ["all"],
  locations: ["all"],
  revenue: ["all"]
}

const fetchAccounts = async (
  tokenInfo: ITokenInfo,
  payload: any,
  linkedTenantId?: number
) => {
  let body = payload
  try {
    const locationsPayload = await queryClient.fetchQuery({
      queryKey: ["orbitshift-locations"],
      queryFn: async () => {
        const res = await getOrbitshiftLocations(tokenInfo)
        return res.data
      }
    })
    const normalized = normalizeLocationFilterValues(
      payload.locations,
      locationsPayload
    )
    body = { ...payload, locations: normalized }
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem("latest_accounts_key")
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          if (JSON.stringify(parsed.locations) !== JSON.stringify(normalized)) {
            parsed.locations = normalized
            sessionStorage.setItem(
              "latest_accounts_key",
              JSON.stringify(parsed)
            )
          }
        } catch {
          void 0
        }
      }
    }
  } catch {
    void 0
  }
  const res = await getAccounts(tokenInfo, body, linkedTenantId)
  return res.data
}
export function useGetAccounts(
  input?: {
    searchText?: string
    industries?: string[]
    locations?: string[]
    revenue?: string[]
  },
  enabled = false
) {
  const location = useLocation()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  const payload = input ?? DEFAULT_PAYLOAD
  const stored =
    typeof window !== "undefined"
      ? sessionStorage.getItem("latest_accounts_key")
      : null
  const restoredPayload = stored
    ? input && JSON.stringify(input) !== stored
      ? input
      : JSON.parse(stored)
    : payload

  if (typeof window !== "undefined" && input) {
    sessionStorage.setItem("latest_accounts_key", JSON.stringify(payload))
  }
  const queryKey = ["accounts", restoredPayload, linkedTenantId]
  return useQuery({
    queryKey,
    queryFn: () => fetchAccounts(tokenInfo!, restoredPayload, linkedTenantId),
    enabled:
      !!tokenInfo && (!!location.pathname.includes("accounts") || enabled)
  })
}

export const useGetLast5EmailMessages = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["recent-email-templates"],
    queryFn: async () => {
      const res = await getLast5EmailTemplates(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetSavedTemplate = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["saved-template"],
    queryFn: async () => {
      const res = await getSavedTemplate(tokenInfo as ITokenInfo)
      return res.data?.data?.details[0] || ""
    },
    enabled: !!tokenInfo
  })
}

export const useGetOrbitshiftLocations = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["orbitshift-locations"],
    queryFn: async () => {
      const res = await getOrbitshiftLocations(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetOrbitshiftLanguages = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["orbitshift-languages"],
    queryFn: async () => {
      const res = await getOrbitshiftLanguages(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetTenantUsersBasicDetails = (enabled: boolean) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["tenant-users-basic-details"],
    queryFn: async () => {
      const res = await getTenantUsersBasicDetails(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetCollaborators = (searchText: string = "") => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["collaborators"],
    queryFn: async () => {
      const res = await getCollaborators(tokenInfo as ITokenInfo, searchText)
      return res.data
    }
  })
}

export const useGetAccountFunctionSegments = (accountId: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["acount-function-segments", accountId],
    queryFn: async () => {
      const res = await getAccountFunctionSegments(
        tokenInfo as ITokenInfo,
        accountId
      )
      return res.data
    },
    enabled: !!tokenInfo
  })
}
export const useGetAccountOfferings = (accountId: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["account-offerings", accountId],
    queryFn: async () => {
      const res = await getAccountOfferings(tokenInfo as ITokenInfo, accountId)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useRequestNewAccount = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useMutation({
    mutationFn: async (payload: {
      account_name: string
      domain: string
      region: string
      note?: string
      source?: string
    }) => {
      const res = await requestNewAccount(tokenInfo as ITokenInfo, payload)
      return res.data
    }
  })
}

const REPORT_REFETCH_INTERVAL_MS = 6 * 60 * 60 * 1000

export const useGetAccountResearchReport = (
  accountId: any,
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["account-research-report", accountId],
    queryFn: async () => {
      const res = await getAccountResearchReport(
        tokenInfo as ITokenInfo,
        accountId
      )
      const data = res.data?.data ?? res.data
      return (
        data?.account_research_report ??
        data?.research_report ??
        data?.report ??
        data ??
        null
      )
    },
    enabled: !!tokenInfo && enabled,
    // "always" would ignore staleTime and refetch on every focus event, even a
    // brief tab switch — plain `true` only refetches on focus once the 6h
    // staleTime below has actually elapsed.
    refetchOnWindowFocus: true,
    // Refetch on a 6h interval
    staleTime: REPORT_REFETCH_INTERVAL_MS,
    refetchInterval: REPORT_REFETCH_INTERVAL_MS
  })
}
